import { TextField, Theme, Tooltip } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import React, { useState, useEffect, useRef } from 'react';
import cx from 'classnames';
import useConfirmDialog from '../../hooks/useConfirmDialog';

interface IPriorityProps {
  priority: number | undefined;
  onEnterValue?: (value: number) => void;
  confirmText?: string;
  isAlerted?: boolean; // turn to red
  errorMessage?: string;
  readOnly?: boolean;
  isRemoving?: boolean;
  onFocus?: () => void;
}

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: 63,
    height: 29,
    margin: 0,
    backgroundColor: theme.palette.background.paper,
  },
  removing: {
    textDecoration: 'line-through',
  },
  inputMarginDense: {
    padding: '7px 8px',
    fontSize: 14,
    color: '#777',
    textAlign: 'center',
  },
  alerted: {
    color: '#f44336',
  },
  editing: {
    color: theme.palette.primary.main,
  },
  alertedOutline: {
    borderColor: '#f44336 !important',
  },
  editingOutline: {
    borderColor: `${theme.palette.primary.main.toString()} !important`,
  },
}));

export const MaxPriority = 9999;

export const Priority = (props: IPriorityProps): JSX.Element => {
  const { priority, onEnterValue, confirmText, isAlerted, errorMessage, readOnly, isRemoving, onFocus } = props;
  const classes = useStyles();
  const confirm = useConfirmDialog();

  const [value, setValue] = useState<number | undefined>(priority);
  const [error, setError] = useState<boolean>(false);

  useEffect(() => {
    setValue(priority);
  }, [priority, setValue]);

  useEffect(() => {
    if (isAlerted) {
      setError(isAlerted);
    }
  }, [isAlerted]);

  const tfRef = React.createRef<HTMLAnchorElement>();

  const priorityBeforeEdit = useRef<number>();

  return (
    <Tooltip disableHoverListener disableTouchListener disableFocusListener open={error} title={errorMessage || ''} placement="top-start">
      <TextField
        variant="outlined"
        value={value && !isNaN(value) ? String(Math.abs(value)).padStart(4, '0') : ''}
        margin="dense"
        error={isAlerted}
        disabled={readOnly || isRemoving}
        type={'number'}
        inputRef={tfRef}
        InputProps={{
          classes: {
            root: cx({ [classes.root]: true, [classes.removing]: isRemoving }),
            inputMarginDense: cx({
              [classes.inputMarginDense]: true,
              [classes.alerted]: isAlerted,
              [classes.editing]: !isAlerted && !readOnly,
            }),
            notchedOutline: isAlerted ? classes.alertedOutline : !readOnly ? classes.editingOutline : '',
          },
        }}
        inputProps={{
          min: 1,
          max: MaxPriority,
        }}
        onChange={e => {
          if (isRemoving) return;
          setValue(Math.min(Number(e.target.value), 9999));
        }}
        onClick={e => {
          if (!readOnly) {
            e.stopPropagation();
          }
        }}
        onFocus={() => {
          if (onFocus) {
            onFocus();
          }

          priorityBeforeEdit.current = Number(value);
        }}
        onBlur={() => {
          if (value && onEnterValue) {
            if (!confirmText) {
              onEnterValue(value);
            } else {
              confirm.open({
                text: confirmText,
                onProceed: () => {
                  onEnterValue(value);
                },
                onCancel: () => {
                  setValue(priorityBeforeEdit.current);
                },
              });
            }
            return;
          }

          if (!value && priorityBeforeEdit.current) {
            setValue(priorityBeforeEdit.current);
            return;
          }
        }}
        onKeyPress={e => {
          if (e.key === 'Enter') {
            tfRef?.current?.blur();
          }
        }}
      />
    </Tooltip>
  );
};
